import { createSlice } from "@reduxjs/toolkit";

import { ALERTS_PAGE_LIMIT } from "../../components/Alerts/constants";

import { defaultInitialState, defaultState } from "../constants";
import { IPayloadAction } from "../rootInterface";

import {
  TCustomLabelCreateOptions,
  TCustomLabelsInfoOptions,
  TLabelsGroupStoreState,
  TLabelsGroupDeleteOptions,
  TLabelsGroupEditOptions,
  TLabelsGroupListOptions,
  TLabelsEntityCategoryOptions,
  TCreateCustomLabelOptions,
  TCreateCustomEntityCategoryOptions,
  TEditCustomEntityCategoryOptions,
  TUpdatePriorityOptions,
  TLabelsEntityCategoryData,
  TLabelsGroupListData,
  TLabelsGroupImportCSVOptions,
} from "./types";

const initialState: TLabelsGroupStoreState = {
  labelsGroup: defaultInitialState,
  labelsGroupInfo: defaultState,
  labelGroupCreate: defaultState,
  labelGroupDelete: defaultState,
  labelsGroupList: defaultInitialState,
  createCustomLabel: defaultState,
  labelsEntityCategory: defaultInitialState,
  editCustomLabel: defaultState,
  deleteCustomLabel: defaultState,
  createCustomEntityCategory: defaultState,
  entityCategoriesOverview: defaultState,
  updatePriority: defaultState,
  importCSVCustomLabel: defaultState,
  labelsGroupExportCSV:defaultState,
};

const labelsGroupSlice = createSlice({
  name: "labels",
  initialState,
  reducers: {
    labelsGroupRequest(state) {
      state.labelsGroup = {
        ...state.labelsGroup,
        fetching: true,
      };
    },
    labelsGroupSuccess(state, action) {
      state.labelsGroup = {
        fetching: false,
        data: action.payload,
        failure: null,
        initialLoading: false,
      };
    },
    labelsGroupFailure(state, action) {
      state.labelsGroup = {
        ...state.labelsGroup,
        fetching: false,
        failure: action.payload,
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelsGroupInfoRequest(state, _: IPayloadAction<TCustomLabelsInfoOptions>) {
      state.labelsGroupInfo = {
        ...state.labelsGroupInfo,
        fetching: true,
      };
    },
    labelsGroupInfoSuccess(state, action) {
      state.labelsGroupInfo = {
        fetching: false,
        data: action.payload,
        failure: null,
      };
    },
    labelsGroupInfoFailure(state, action) {
      state.labelsGroupInfo = {
        ...state.labelsGroupInfo,
        fetching: false,
        failure: action.payload,
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelGroupCreateRequest(state, _: IPayloadAction<TCustomLabelCreateOptions>) {
      state.labelGroupCreate = {
        ...state.labelGroupCreate,
        fetching: true,
      };
    },
    labelGroupCreateSuccess(state, action) {
      state.labelGroupCreate = {
        fetching: false,
        data: action.payload,
        failure: null,
      };
    },
    labelGroupCreateFailure(state, action) {
      state.labelGroupCreate = {
        ...state.labelGroupCreate,
        fetching: false,
        failure: action.payload,
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelsGroupDeleteRequest(state, _: IPayloadAction<TLabelsGroupDeleteOptions>) {
      state.labelGroupDelete = {
        ...state.labelGroupDelete,
        fetching: true,
      };
    },
    labelsGroupDeleteSuccess(state) {
      state.labelGroupDelete = {
        fetching: false,
        data: null,
        failure: null,
      };
    },
    labelsGroupDeleteFailure(state, action) {
      state.labelGroupDelete = {
        ...state.labelGroupDelete,
        fetching: false,
        failure: action.payload,
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelsGroupLabelsEditRequest(state, _: IPayloadAction<TLabelsGroupEditOptions>) {
      state.labelsGroup = {
        ...state.labelsGroup,
        fetching: true,
      };
    },
    labelsGroupLabelsEditSuccess(state, action) {
      state.labelsGroup.fetching = false;
      state.labelsGroup.failure = null;
      state.labelsGroup.initialLoading = false;
      const updatedItem = action.payload;
      if (state.labelsGroup.data?.results) {
        state.labelsGroup.data.results = state.labelsGroup.data.results.map(item =>
          item.id === updatedItem.id ? { ...item, ...updatedItem } : item
        );
      }
    },
    labelsGroupLabelsEditFailure(state, action) {
      state.labelsGroup = {
        ...state.labelsGroup,
        fetching: false,
        failure: action.payload,
        initialLoading: false,
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelsGroupListRequest(state, _: IPayloadAction<TLabelsGroupListOptions>) {
      state.labelsGroupList = {
        ...state.labelsGroupList,
        fetching: true,
      };
    },
    labelsGroupListSuccess(
      state,
      action: IPayloadAction<{
        data: TLabelsGroupListData;
        infiniteScroll?: boolean;
      }>
    ) {
      let newData = action.payload.data;
      if (action.payload.infiniteScroll) {
        const prevResults = state?.labelsGroupList?.data?.results || [];
        newData = {
          ...action.payload.data,
          results: [...prevResults, ...(action.payload?.data?.results || [])],
        };
      }
      state.labelsGroupList = {
        fetching: false,
        data: newData,
        failure: null,
        initialLoading: false,
      };
    },
    labelsGroupListFailure(state, action) {
      state.labelsGroupList = {
        ...state.labelsGroupList,
        fetching: false,
        failure: action.payload,
        initialLoading: false,
      };
    },
    labelsGroupListClear(state) {
      state.labelsGroupList = defaultInitialState;
    },
    createCustomLabelRequest(state, _: IPayloadAction<TCreateCustomLabelOptions>) {
      state.labelsGroupList = {
        ...state.labelsGroupList,
        fetching: true,
      };
    },
    createCustomLabelSuccess(state, action) {
      const newItem = action.payload;
      const existingData = state.labelsGroupList.data;
      const pages = existingData &&  Math.ceil((existingData.count + 1) / ALERTS_PAGE_LIMIT);
      state.labelsGroupList = {
        fetching: false,
        failure: null,
        initialLoading: false,
        data: existingData
          ? {
            ...existingData,
            count: existingData.count + 1,
            next: pages && pages > 1 ? pages : null,
            pages,
            results: [newItem, ...existingData.results],
          }
          : {
            count: 1,
            next: null,
            previous: null,
            pages: null,
            results: [newItem],
          },
      };
    },
    createCustomLabelFailure(state, action) {
      state.labelsGroupList = {
        ...state.labelsGroupList,
        fetching: false,
        failure: action.payload,
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelsEntityCategoryRequest(state, _: IPayloadAction<TLabelsEntityCategoryOptions>) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: true,
      };
    },
    labelsEntityCategorySuccess(
      state,
      action: IPayloadAction<{
        data: TLabelsEntityCategoryData;
        infiniteScroll?: boolean;
      }>
    ) {
      let newData = action.payload.data;
      if (action.payload.infiniteScroll) {
        const prevResults = state?.labelsEntityCategory?.data?.results || [];
        newData = {
          ...action.payload.data,
          results: [...prevResults, ...(action.payload?.data?.results || [])],
        };
      }
      state.labelsEntityCategory = {
        fetching: false,
        data: newData,
        failure: null,
        initialLoading: false,
      };
    },
    labelsEntityCategoryFailure(state, action) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: false,
        failure: action.payload,
        initialLoading: false,
      };
    },
    labelsEntityCategoryClear(state) {
      state.labelsEntityCategory = defaultInitialState;
    },
    editCustomLabelRequest(state, action) {
      const { id } = action.payload;
      state.editCustomLabel = {
        ...state.editCustomLabel,
        fetching: true,
        fetchingById: {
          ...(state.editCustomLabel.fetchingById || {}),
          [id]: true,
        },
      };
    },
    editCustomLabelSuccess(state, action) {
      const { id } = action.payload;
      state.labelsGroupList = {
        ...state.labelsGroupList,
        fetching: false,
        failure: null,
      };
      state.editCustomLabel.fetchingById = {
        ...(state.editCustomLabel.fetchingById || {}),
        [id]: false,
      };

      const updatedItem = action.payload;
      const entityCategoryLookup = state.labelsEntityCategory.data?.results;
      if (state.labelsGroupList.data?.results) {
        state.labelsGroupList.data.results = state.labelsGroupList.data.results.map(item => {
          if (item.id === updatedItem.id) {
            const matchedCategory = entityCategoryLookup?.find(category => category.id === updatedItem.entity_category);
            if (matchedCategory) {
              return {
                ...item,
                ...updatedItem,
                entity_category_name: matchedCategory.name,
              };
            }
            return {
              ...item,
              ...updatedItem,
            };
          }
          return item;
        });
      }

      state.editCustomLabel = {
        data: updatedItem,
        fetching: false,
        failure: null,
      };
    },
    editCustomLabelFailure(state, action) {
      const { id } = action.payload;
      if (!state.editCustomLabel.fetchingById) {
        state.editCustomLabel.fetchingById = {};
      }
      state.editCustomLabel.fetchingById[id] = false;
      state.editCustomLabel = {
        ...state.editCustomLabel,
        fetching: false,
        failure: action.payload,
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteCustomLabelRequest(state, _: IPayloadAction<{ id: string; callOnSuccess?: () => void }>) {
      state.deleteCustomLabel = {
        ...state.deleteCustomLabel,
        fetching: true,
      };
    },
    deleteCustomLabelSuccess(state) {
      state.deleteCustomLabel = {
        fetching: false,
        data: null,
        failure: null,
      };
    },
    deleteCustomLabelFailure(state, action) {
      state.deleteCustomLabel = {
        ...state.deleteCustomLabel,
        fetching: false,
        failure: action.payload,
      };
    },
    deleteEntityCategoryRequest(state, _: IPayloadAction<{ id: string; callOnSuccess?: () => void }>) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: true,
      };
    },
    deleteEntityCategorySuccess(state, action) {
      state.labelsEntityCategory.fetching = false;
      state.labelsEntityCategory.failure = null;

      const deletedItemId = action.payload.id;

      if (state.labelsEntityCategory.data?.results) {
        state.labelsEntityCategory.data.results = state.labelsEntityCategory.data.results.filter(
          item => item.id !== deletedItemId
        );
        state.labelsEntityCategory.data.count = Math.max(0, state.labelsEntityCategory.data.count - 1);
      }
    },
    deleteEntityCategoryFailure(state, action) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: false,
        failure: action.payload,
      };
    },
    createCustomEntityCategoryRequest(state, _: IPayloadAction<TCreateCustomEntityCategoryOptions>) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: true,
      };
    },
    createCustomEntityCategorySuccess(state, action) {
      const newItem = action.payload;
      const existingData = state.labelsEntityCategory.data;

      state.labelsEntityCategory = {
        fetching: false,
        failure: null,
        initialLoading: false,
        data: existingData
          ? {
            ...existingData,
            count: existingData.count + 1,
            results: [newItem, ...existingData.results],
          }
          : {
            count: 1,
            next: null,
            previous: null,
            pages: null,
            results: [newItem],
          },
      };
      const currentOverview = state.entityCategoriesOverview?.data;
      if (currentOverview) {
        state.entityCategoriesOverview = {
          fetching: false,
          failure: null,
          data: {
            ...currentOverview,
            custom_categories_count: currentOverview.custom_categories_count + 1,
          },
        };
      }
    },
    createCustomEntityCategoryFailure(state, action) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: false,
        failure: action.payload,
      };
    },
    editCustomEntityCategoryRequest(state, _: IPayloadAction<TEditCustomEntityCategoryOptions & { id: string }>) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: true,
      };
    },
    editCustomEntityCategorySuccess(state, action) {
      state.labelsEntityCategory.fetching = false;
      state.labelsEntityCategory.failure = null;

      const updatedItem = action.payload;

      if (state.labelsEntityCategory.data?.results) {
        state.labelsEntityCategory.data.results = state.labelsEntityCategory.data.results.map(item =>
          item.id === updatedItem.id ? { ...item, ...updatedItem } : item
        );
      }
    },
    editCustomEntityCategoryFailure(state, action) {
      state.labelsEntityCategory = {
        ...state.labelsEntityCategory,
        fetching: false,
        failure: action.payload,
      };
    },
    entityCategoriesOverviewRequest(state) {
      state.entityCategoriesOverview = {
        ...state.entityCategoriesOverview,
        fetching: true,
      };
    },
    entityCategoriesOverviewSuccess(state, action) {
      state.entityCategoriesOverview = {
        fetching: false,
        data: action.payload,
        failure: null,
      };
    },
    entityCategoriesOverviewFailure(state, action) {
      state.entityCategoriesOverview = {
        ...state.entityCategoriesOverview,
        fetching: false,
        failure: action.payload,
      };
    },
    updatePriorityRequest(state, _: IPayloadAction<TUpdatePriorityOptions>) {
      state.labelsGroup = {
        ...state.labelsGroup,
        fetching: true,
      };
    },
    updatePrioritySuccess(state, action) {
      state.labelsGroup.fetching = false;
      state.labelsGroup.failure = null;
      const updatedItem = action.payload;
      if (state.labelsGroup.data?.results) {
        const currentList = [...state.labelsGroup.data.results];
        const filteredList = currentList.filter(item => item.id !== updatedItem.id);
        filteredList.splice(updatedItem.priority, 0, updatedItem);
        state.labelsGroup.data.results = filteredList.map((item, index) => ({
          ...item,
          priority: index,
        }));
      }
    },
    updatePriorityFailure(state, action) {
      state.labelsGroup = {
        ...state.labelsGroup,
        fetching: false,
        failure: action.payload,
      };
    },
    importCSVRequest(state, _: IPayloadAction<TLabelsGroupImportCSVOptions>) {
      state.importCSVCustomLabel = {
        ...state.importCSVCustomLabel,
        fetching: true,
        failure: null,
      };
    },
    importCSVRequestClean(state) {
      state.importCSVCustomLabel = {
        ...state.importCSVCustomLabel,
        fetching: false,
        failure: null,
      };
    },
    importCSVSuccess(state, action) {
      state.importCSVCustomLabel = {
        fetching: false,
        data: action.payload,
        failure: null,
      };
    },
    importCSVFailure(state, action) {
      state.importCSVCustomLabel = {
        ...state.importCSVCustomLabel,
        fetching: false,
        failure: action.payload,
      };
    },
    labelsGroupExportCSVRequest(state, _: IPayloadAction<TLabelsGroupEditOptions>) {
      state.labelsGroupExportCSV = {
        ...state.labelsGroupExportCSV,
        fetching: true,
      };
    },
    labelsGroupExportCSVSuccess(state, action) {
      state.labelsGroupExportCSV = {
        fetching: false,
        data: action.payload,
        failure: null,
      };
    },
    labelsGroupExportCSVFailure(state, action) {
      state.labelsGroupExportCSV = {
        ...state.labelsGroupExportCSV,
        fetching: false,
        failure: action.payload,
      };
    },
  },
});

export const {
  labelsGroupRequest,
  labelsGroupSuccess,
  labelsGroupFailure,
  labelsGroupInfoRequest,
  labelsGroupInfoSuccess,
  labelsGroupInfoFailure,
  labelGroupCreateRequest,
  labelGroupCreateSuccess,
  labelGroupCreateFailure,
  labelsGroupDeleteRequest,
  labelsGroupDeleteSuccess,
  labelsGroupDeleteFailure,
  labelsGroupLabelsEditRequest,
  labelsGroupLabelsEditSuccess,
  labelsGroupLabelsEditFailure,
  labelsGroupListRequest,
  labelsGroupListSuccess,
  labelsGroupListFailure,
  labelsGroupListClear,
  createCustomLabelRequest,
  createCustomLabelSuccess,
  createCustomLabelFailure,
  labelsEntityCategoryRequest,
  labelsEntityCategorySuccess,
  labelsEntityCategoryFailure,
  labelsEntityCategoryClear,
  editCustomLabelRequest,
  editCustomLabelSuccess,
  editCustomLabelFailure,
  deleteCustomLabelRequest,
  deleteCustomLabelSuccess,
  deleteCustomLabelFailure,
  deleteEntityCategoryRequest,
  deleteEntityCategorySuccess,
  deleteEntityCategoryFailure,
  createCustomEntityCategoryRequest,
  createCustomEntityCategorySuccess,
  createCustomEntityCategoryFailure,
  editCustomEntityCategoryRequest,
  editCustomEntityCategorySuccess,
  editCustomEntityCategoryFailure,
  entityCategoriesOverviewRequest,
  entityCategoriesOverviewSuccess,
  entityCategoriesOverviewFailure,
  updatePriorityRequest,
  updatePrioritySuccess,
  updatePriorityFailure,
  importCSVRequest,
  importCSVSuccess,
  importCSVFailure,
  importCSVRequestClean,
  labelsGroupExportCSVRequest,
  labelsGroupExportCSVSuccess,
  labelsGroupExportCSVFailure
} = labelsGroupSlice.actions;

export default labelsGroupSlice.reducer;

